import { useEffect, useState } from "react";
import { X, Link as LinkIcon, MessageCircle, MessageSquare, Check } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_URL =
  typeof window !== "undefined"
    ? window.location.origin
    : "";
const DEFAULT_TEXT = "Shift How You Financially Think — join The SHYFT Coalition™.";

const trackShare = (channel, url) => {
  // Fire-and-forget PostHog event
  try {
    if (typeof window !== "undefined" && window.posthog) {
      window.posthog.capture("share_clicked", { channel, url });
    }
  } catch {
    /* silent */
  }
  // Increment the daily counter on our backend
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (base) {
      fetch(`${base}/api/share/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channel }),
      }).catch(() => {});
    }
  } catch {
    /* silent */
  }
};

const stripScheme = (url) => url.replace(/^https?:\/\//, "").replace(/\/$/, "");

export const ShareModal = ({
  open,
  onClose,
  url = DEFAULT_URL,
  text = DEFAULT_TEXT,
  title = null,
  description = null,
}) => {
  const [copied, setCopied] = useState(false);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard");
      trackShare("copy_link", url);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — please try again");
    }
  };

  const waHref = `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
  const smsHref = `sms:?&body=${encodeURIComponent(`${text} ${url}`)}`;

  const channels = [
    {
      id: "copy",
      label: copied ? "Copied" : "Copy Link",
      Icon: copied ? Check : LinkIcon,
      onClick: handleCopy,
      testid: "share-channel-copy",
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      Icon: MessageCircle,
      href: waHref,
      onTrack: () => trackShare("whatsapp", url),
      testid: "share-channel-whatsapp",
    },
    {
      id: "text",
      label: "Text",
      Icon: MessageSquare,
      href: smsHref,
      onTrack: () => trackShare("text", url),
      testid: "share-channel-text",
    },
  ];

  return (
    <div
      data-testid="share-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-modal-title"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close share dialog"
        data-testid="share-modal-backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm cursor-default"
      />

      {/* Card */}
      <div
        className="relative w-full max-w-sm rounded-[1.75rem] border border-[rgba(201,162,91,0.35)] bg-gradient-to-b from-[#14110e] to-[#0a0a0a] p-7 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.85)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          data-testid="share-modal-close"
          aria-label="Close"
          className="absolute top-4 right-4 inline-flex items-center justify-center w-8 h-8 rounded-full border border-[rgba(201,162,91,0.25)] text-[#cfc8b9] hover:text-white hover:border-[rgba(201,162,91,0.55)] transition"
        >
          <X size={14} />
        </button>

        {/* Title */}
        <div className="text-center">
          <span className="eyebrow !text-[#6fb3ff]">Share</span>
          <h3
            id="share-modal-title"
            className="font-serif-display mt-2 text-xl text-white leading-tight"
          >
            {title || (
              <>
                Share The <span className="text-gradient-gold">SHYFT</span>
              </>
            )}
          </h3>
          <p className="mt-2 text-[#b8b3aa] text-xs leading-relaxed">
            {description ||
              "Pass the link to someone who needs to SHYFT how they financially think."}
          </p>
        </div>

        {/* URL pill */}
        <div className="mt-5 flex items-center gap-2 rounded-full border border-[rgba(201,162,91,0.25)] bg-[#0a0a0a] px-4 py-2.5">
          <LinkIcon size={13} className="text-gold shrink-0" />
          <span
            data-testid="share-modal-url"
            className="flex-1 truncate text-[#e7e1d4] text-[13px] tracking-wide"
          >
            {stripScheme(url)}
          </span>
          <button
            type="button"
            onClick={handleCopy}
            data-testid="share-modal-copy-inline"
            className={`text-[11px] uppercase tracking-[0.18em] font-semibold transition ${
              copied ? "text-[#6fb3ff]" : "text-[#e7c98a] hover:text-white"
            }`}
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        {/* Channels */}
        <div className="mt-6 grid grid-cols-3 gap-3">
          {channels.map(({ id, label, Icon, onClick, href, testid }) => {
            const inner = (
              <>
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-[rgba(201,162,91,0.3)] bg-[#0a0a0a] group-hover:border-[rgba(30,144,255,0.55)] transition">
                  <Icon size={17} className="text-gold group-hover:text-[#6fb3ff] transition" />
                </span>
                <span className="mt-2 text-[11px] uppercase tracking-[0.18em] text-[#cfc8b9] group-hover:text-white transition">
                  {label}
                </span>
              </>
            );

            if (href) {
              return (
                <a
                  key={id}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={testid}
                  onClick={() => channels.find((c) => c.id === id)?.onTrack?.()}
                  className="group flex flex-col items-center text-center"
                >
                  {inner}
                </a>
              );
            }
            return (
              <button
                key={id}
                type="button"
                onClick={onClick}
                data-testid={testid}
                className="group flex flex-col items-center text-center"
              >
                {inner}
              </button>
            );
          })}
        </div>

        <div className="vault-divider mt-6" />
        <p className="mt-4 text-center text-[10px] tracking-[0.2em] uppercase text-[#80786c]">
          SHYFT Coalition™ · Vault
        </p>
      </div>
    </div>
  );
};

export default ShareModal;
